<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      ref="overlayEl"
      class="rp-receipt-preview-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rp-receipt-preview-title"
      tabindex="-1"
      @click.self="onClose"
      @keydown.escape.prevent="onClose"
    >
      <div class="rp-receipt-preview-dialog" @click.stop>
        <header class="rp-receipt-preview-header">
          <h2 id="rp-receipt-preview-title">Receipt preview</h2>
          <button type="button" class="rp-receipt-preview-close" aria-label="Close" @click="onClose">
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>
        <div class="rp-receipt-preview-frame-wrap">
          <iframe
            v-if="iframeSrcdoc"
            class="rp-receipt-preview-iframe"
            title="Receipt preview"
            :srcdoc="iframeSrcdoc"
          ></iframe>
        </div>
        <footer class="rp-receipt-preview-footer">
          <button type="button" class="rp-receipt-preview-btn rp-receipt-preview-btn--ghost" @click="onClose">
            Close
          </button>
          <button
            type="button"
            class="rp-receipt-preview-btn rp-receipt-preview-btn--primary"
            :disabled="!canDownload || downloading"
            @click="onDownload"
          >
            <i class="bi bi-file-earmark-arrow-down" aria-hidden="true"></i>
            {{ downloading ? 'Preparing PDF…' : 'Download PDF' }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script>
import './ReceivePaymentReceiptPreviewModal.css';
import ApiService from '@/services/api.service';
import {
  buildReceivePaymentReceiptPdfHtml,
  base64ToPdfBlob,
  triggerPdfDownload,
} from '@/utils/receivePaymentReceiptPdf.js';

export default {
  name: 'ReceivePaymentReceiptPreviewModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      default: null,
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      downloading: false,
      iframeSrcdoc: '',
    };
  },
  computed: {
    canDownload() {
      return !!(this.group?.receivePaymentId && this.companyId);
    },
    companyName() {
      return (
        this.$store.state.currentCompany?.name ||
        this.$store.state.currentCompany?.companyName ||
        'Company'
      );
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) {
          this.refreshIframe();
          document.body.style.overflow = 'hidden';
          this.$nextTick(() => {
            this.$refs.overlayEl?.focus?.();
          });
        } else {
          document.body.style.overflow = '';
          this.iframeSrcdoc = '';
        }
      },
    },
    group: {
      deep: true,
      handler() {
        if (this.modelValue && this.group) {
          this.refreshIframe();
        }
      },
    },
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
  methods: {
    refreshIframe() {
      if (!this.group) {
        this.iframeSrcdoc = '';
        return;
      }
      this.iframeSrcdoc = buildReceivePaymentReceiptPdfHtml({
        companyName: this.companyName,
        group: this.group,
      });
    },
    onClose() {
      this.$emit('update:modelValue', false);
    },
    async onDownload() {
      if (!this.canDownload || this.downloading) return;
      const paymentId = this.group.receivePaymentId;
      const htmlContent = buildReceivePaymentReceiptPdfHtml({
        companyName: this.companyName,
        group: this.group,
      });
      this.downloading = true;
      try {
        const res = await ApiService.post(
          `/customer/receive-payments/${paymentId}/generate-notification-pdf`,
          { companyId: this.companyId, htmlContent }
        );
        const d = res.data?.data || res.data;
        const pdfBase64 = d?.pdfBase64;
        const pdfFileName = d?.pdfFileName || `receipt-${paymentId}.pdf`;
        if (!pdfBase64) {
          throw new Error('No PDF returned');
        }
        const blob = base64ToPdfBlob(pdfBase64);
        triggerPdfDownload(blob, pdfFileName);
        this.$toast?.success?.('Receipt downloaded');
        this.onClose();
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to generate receipt';
        this.$toast?.error?.(msg);
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>
